import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Database, ListTree, Loader2, Save, Trash2, Undo2 } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import apiReminderBuilder from "../../api";
import useConfirm from "../../components/useConfirm";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage, isCautionMessage } from "../../features/builder.utils";
import apiReminderSourceField from "./api";
import { MAX_FIELDS } from "./features/source-field.constants";
import { sameValue } from "./features/source-field.utils";
import { sourceFieldFilterFields } from "./features/source-field.filter";
import { computedFieldFields } from "./features/computed-field.fields";
import { computedFieldValidation } from "./features/computed-field.validation";
import { makeComputedFieldLoader } from "./features/computed-field.dependencies";
import { toComputedFieldPayload } from "./features/computed-field.utils";
import { getFieldColumns } from "./source-field-list/fieldColumns";
import FieldRowActions from "./source-field-list/FieldRowActions";
import SourceSummary from "./source-field-list/SourceSummary";
import FieldViewDrawer from "./source-field-dialogs/FieldViewDrawer";

const SOURCE_PARAM = "source";
const INITIAL_QUERY = { searchText: "", search: {}, sort: { key: "sort_order", direction: "asc" } };
const INITIAL_PAGE = { number: 1, size: 25 };

// Reminder Setup -> Source Field (tbl_rem_source_field): the fields of one source and what each means. Label, role,
// format and flags are edited right in the grid and saved together; computed fields are added in a form.
const SourceField = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceId = Number(searchParams.get(SOURCE_PARAM)) || null;
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(INITIAL_QUERY);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [reloadKey, setReloadKey] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [selectionReset, setSelectionReset] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [fillingId, setFillingId] = useState(null);
  const [form, setForm] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const dirtyCount = Object.keys(edits).length;
  const hasQuery = Boolean(query.searchText.trim()) || Object.keys(query.search).length > 0;

  // ── Data Fetching ─────────────────────────────────────────────────────────

  useEffect(() => {
    let active = true;
    apiReminderBuilder
      .getSourceOptions()
      .then((res) => active && setSources(res.data?.data || []))
      .catch((err) => active && showAlert({ type: "error", message: errorMessage(err, "Failed to load sources.") }))
      .finally(() => active && setSourcesLoading(false));
    return () => {
      active = false;
    };
  }, [showAlert]);

  // The chosen source, reloaded after every save: a change can move it back to DRAFT.
  useEffect(() => {
    if (!sourceId) {
      setSource(null);
      return undefined;
    }
    let active = true;
    apiReminderBuilder
      .getSource(sourceId)
      .then((res) => active && setSource(res.data?.data ?? null))
      .catch((err) => {
        if (!active) return;
        setSource(null);
        showAlert({ type: "error", message: errorMessage(err, "Could not load this source.") });
      });
    return () => {
      active = false;
    };
  }, [sourceId, reloadKey, showAlert]);

  // Every field of the source (at most 300), so the grid pages, edits and saves them on this page.
  useEffect(() => {
    if (!sourceId) {
      setRows([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    apiReminderSourceField
      .getAll({
        source_fid: sourceId,
        searchText: query.searchText.trim(),
        search: JSON.stringify(query.search),
        sortBy: query.sort.key,
        sortOrder: query.sort.direction,
        limit: MAX_FIELDS,
      })
      .then((res) => active && setRows(res.data?.data || []))
      .catch((err) => active && showAlert({ type: "error", message: errorMessage(err, "Failed to load fields.") }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [sourceId, query, reloadKey, showAlert]);

  // ── Grid edits ────────────────────────────────────────────────────────────

  const patch = useCallback((row, change) => {
    setEdits((current) => {
      const next = { ...(current[row.id] ?? {}), ...change };
      // A value set back to the saved one is no longer a change.
      for (const key of Object.keys(next)) if (sameValue(next[key], row[key])) delete next[key];
      const copy = { ...current };
      if (Object.keys(next).length) copy[row.id] = next;
      else delete copy[row.id];
      return copy;
    });
  }, []);

  const valueOf = useCallback((row, key) => (edits[row.id] && key in edits[row.id] ? edits[row.id][key] : row[key]), [edits]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const res = await apiReminderSourceField.bulkUpdate(Object.entries(edits).map(([id, change]) => ({ id: Number(id), ...change })));
      const message = res.data?.message || "Fields saved.";
      showAlert({ type: isCautionMessage(message) ? "warning" : "success", message });
      setEdits({});
      reload();
    } catch (err) {
      showAlert({ type: "error", title: "Not saved", message: errorMessage(err, "The changes could not be saved.") });
    } finally {
      setSaving(false);
    }
  };

  const fillValues = useCallback(
    async (row) => {
      setFillingId(row.id);
      try {
        const res = await apiReminderSourceField.getDistinctValues(sourceId, row.field_key);
        const values = res.data?.data ?? [];
        patch(row, { enum_values: values.length ? values : null });
        showAlert({ type: "info", message: values.length ? `${values.length} value(s) found for ${row.field_key}. Save changes to keep them.` : `No values found for ${row.field_key}.` });
      } catch (err) {
        showAlert({ type: "error", message: errorMessage(err, "Could not read the values.") });
      } finally {
        setFillingId(null);
      }
    },
    [sourceId, patch, showAlert]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const chooseSource = (id) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set(SOURCE_PARAM, String(id));
        else next.delete(SOURCE_PARAM);
        return next;
      },
      { replace: true }
    );
    setEdits({});
    setQuery(INITIAL_QUERY);
    setPage(INITIAL_PAGE);
    setResetTrigger((key) => key + 1);
    setSelectionReset((key) => key + 1);
  };

  const handleRefresh = () => {
    setQuery(INITIAL_QUERY);
    setPage(INITIAL_PAGE);
    setResetTrigger((key) => key + 1);
    setSelectionReset((key) => key + 1);
    reload();
  };

  const deleteFields = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Field",
      message: `Delete ${ids.length > 1 ? `${ids.length} fields` : "this field"}? Fields used by a reminder trigger can't be deleted, and an active source moves back to DRAFT.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderSourceField.delete(ids);
      const message = res.data?.message || "Deleted successfully.";
      showAlert({ type: isCautionMessage(message) ? "warning" : "success", message });
      setEdits((current) => Object.fromEntries(Object.entries(current).filter(([id]) => !ids.includes(Number(id)))));
      setSelectionReset((key) => key + 1);
      reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const closeForm = () => setForm(null);
  const afterFormSave = () => {
    if (form?.id) setEdits((current) => Object.fromEntries(Object.entries(current).filter(([id]) => Number(id) !== form.id)));
    reload();
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const editable = canManage && Boolean(source);
  const dirtyIds = useMemo(() => new Set(Object.keys(edits).map(Number)), [edits]);
  const columns = useMemo(
    () => getFieldColumns({ editable, subjectType: source?.subject_type, dirtyIds, valueOf, patch, fillingId, onFill: fillValues }),
    [editable, source?.subject_type, dirtyIds, valueOf, patch, fillingId, fillValues]
  );
  const computedLoader = useMemo(() => (source ? makeComputedFieldLoader(source) : null), [source]);

  const pageCount = Math.max(1, Math.ceil(rows.length / page.size));
  const pageNumber = Math.min(page.number, pageCount);
  const pageRows = rows.slice((pageNumber - 1) * page.size, pageNumber * page.size);

  const renderRowActions = (row) => (
    <FieldRowActions row={row} canManage={canManage} onView={setViewRecord} onEditComputed={(target) => setForm({ id: target.id })} onDelete={(target) => deleteFields([target.id])} />
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const emptyState = () => {
    if (!sourcesLoading && !sources.length) {
      return { icon: Database, title: "No sources yet", message: "Create a source on the Source tab first; the columns you tick there become its fields here." };
    }
    if (!sourceId) return { icon: Database, title: "Choose a source", message: "Its fields are what reminders may read. Label them, give them roles and formats right in the grid, then save." };
    return null;
  };
  const empty = emptyState();

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <SourceSummary sources={sources} sourcesLoading={sourcesLoading} sourceId={sourceId} onChoose={chooseSource} source={source} locked={dirtyCount > 0} />

        {empty ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState icon={empty.icon} title={empty.title} message={empty.message} showBack={false} showRefresh={false} />
          </div>
        ) : (
          <>
            <TableToolBar
              onSearch={(searchText) => {
                setQuery((current) => ({ ...current, searchText }));
                setPage((current) => ({ ...current, number: 1 }));
              }}
              onRefresh={handleRefresh}
              onCreate={() => setForm({ id: null })}
              onDeleteSelected={canManage ? () => deleteFields(selectedRows) : undefined}
              selectedCount={selectedRows.length}
              resetSearchTrigger={resetTrigger}
              canCreate={canManage && Boolean(source)}
              resultCount={rows.length}
              advanceFilter={
                <GlobalAdvanceFilter
                  fields={sourceFieldFilterFields}
                  onApply={({ search }) => {
                    setQuery((current) => ({ ...current, search: search || {} }));
                    setPage((current) => ({ ...current, number: 1 }));
                  }}
                  resetKey={resetTrigger}
                />
              }
            />

            {dirtyCount > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning_border bg-warning_bg px-3 py-2">
                <span className="text-xs text-text_primary">{dirtyCount} field(s) changed. Nothing is saved until you click Save changes.</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEdits({})}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text_secondary disabled:opacity-50"
                  >
                    <Undo2 size={13} /> Discard
                  </button>
                  <button type="button" onClick={saveChanges} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save changes
                  </button>
                </div>
              </div>
            )}

            {!loading && rows.length === 0 ? (
              <div className="rounded-lg border border-border bg-surface">
                <NoDataState
                  icon={ListTree}
                  title={hasQuery ? "No fields match" : "No fields yet"}
                  message={hasQuery ? "No fields match your search or filters. Try adjusting them." : "Tick columns of this source on the Source tab, or add a computed field."}
                  showBack={false}
                  showRefresh={hasQuery}
                  onRefresh={handleRefresh}
                  showCreate={canManage && !hasQuery && Boolean(source)}
                  createText="Add Computed Field"
                  onCreate={() => setForm({ id: null })}
                />
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-lg border border-border">
                  <TableGrid
                    data={pageRows}
                    loading={loading}
                    columns={columns}
                    pageSize={page.size}
                    sortConfig={query.sort}
                    onSort={(key) =>
                      setQuery((current) => ({ ...current, sort: { key, direction: current.sort.key === key && current.sort.direction === "asc" ? "desc" : "asc" } }))
                    }
                    onSelectionChange={setSelectedRows}
                    resetSelectionTrigger={selectionReset}
                    selectable={canManage}
                    showActions
                    renderActions={renderRowActions}
                  />
                </div>
                <Pagination
                  totalCount={rows.length}
                  pageSize={page.size}
                  currentPage={pageNumber}
                  onPageChange={(number, newSize) => setPage((current) => (newSize && newSize !== current.size ? { number: 1, size: newSize } : { ...current, number }))}
                  selectedCount={selectedRows.length}
                />
                <p className="text-[11px] text-text_muted">
                  One SUBJECT (a user id) and at most one REFERENCE and one PROJECT_REF per source. USER_REF holds a user id or a list of them, EMAIL_REF an email or a list. Sensitive fields are never
                  template variables. Changing a role, a data type or what a field reads moves an active source back to DRAFT.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {computedLoader && (
        <EntityFormModal
          isOpen={Boolean(form)}
          onClose={closeForm}
          id={form?.id}
          title="Computed Field"
          fetchById={apiReminderSourceField.getById}
          createFn={(values) => apiReminderSourceField.create(toComputedFieldPayload(values, sourceId))}
          updateFn={(id, values) => apiReminderSourceField.update(id, toComputedFieldPayload(values))}
          dependenciesLoader={computedLoader}
          fields={computedFieldFields}
          validationSchema={computedFieldValidation}
          onSuccess={afterFormSave}
        />
      )}

      {viewRecord && <FieldViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default SourceField;
